import type { MerchantProduct as ApiMerchantProduct } from '../types'

/**
 * Calculate total stock from variant combinations
 */
export function calculateTotalStock(variantCombinations: ApiMerchantProduct['variantCombinations']): number {
  if (!variantCombinations) return 0
  return variantCombinations.reduce((sum, variant) => sum + (variant?.stock || 0), 0)
}

/**
 * Determine stock status based on quantity
 */
export function getStockStatus(totalStock: number): 'in-stock' | 'low-stock' | 'out-of-stock' | 'warning' {
  if (totalStock === 0) return 'out-of-stock'
  if (totalStock < 20) return 'warning'
  return 'in-stock'
}

/**
 * Get current price from product data
 */
export function getCurrentPrice(merchantProduct: ApiMerchantProduct): number {
  return merchantProduct.pricingMetrics?.averageSellingPrice || 
    (merchantProduct.variantCombinations?.length > 0 ? merchantProduct.variantCombinations[0]?.price : 0) || 
    0
}

/**
 * Extract primary category from category array
 */
export function getPrimaryCategory(categories: ApiMerchantProduct['category']): string {
  return categories?.length > 0 ? categories[0].id : 'Uncategorized'
}

/**
 * Calculate price range from variant combinations
 */
export function calculatePriceRange(variantCombinations: ApiMerchantProduct['variantCombinations']): {
  min: number
  max: number
} | undefined {
  if (!variantCombinations || variantCombinations.length <= 1) {
    return undefined
  }
  
  return {
    min: Math.min(...variantCombinations.map(v => v?.price || 0)),
    max: Math.max(...variantCombinations.map(v => v?.price || 0))
  }
}

/**
 * Transform variant combinations for UI display
 */
export function transformVariantCombinationsForUI(
  variantCombinations: ApiMerchantProduct['variantCombinations'],
  variantOptions: ApiMerchantProduct['variantOptions'],
  resolveImageFn: (combination: any, options: any) => string | undefined
): any[] {
  if (!variantCombinations) return []
  
  return variantCombinations.map(variant => ({
    id: variant.id || '',
    sku: variant.sku || '',
    isActive: variant.isActive ?? true,
    stock: variant.stock || 0,
    price: variant.price || 0,
    weight: variant.weight,
    tierVariation: variant.tierVariation || [],
    image: resolveImageFn(variant, variantOptions)
  }))
}